import type { VoidComponent } from "solid-js";
import { createSignal, mergeProps, Show, splitProps } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { IconButton, TextAndIconButton } from "../button/Button";
import type { MediaModalProps } from "../modal/MediaModal";
import MediaModal from "../modal/MediaModal";
import { isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import type { CustomerResource } from "../../services/CustomerResourceService";
import { DECORATIVE_IMAGE_ALT } from "../../constants";
import { useModalManager } from "../modal/ModalManager";
import brokenImage from "../../assets/editor/broken-image.svg";
import Text from "../text/Text.tsx";

type ImageInputProps = {
  fixedSize?: boolean;
  onClear: () => void;
  value?: CustomerResource | null;
  buttonLabel: string;
} & Omit<MediaModalProps, "open" | "setOpen"> &
  ExtendableInputProps;

const ImageInput: VoidComponent<ImageInputProps> = (rawProps) => {
  const props = mergeProps({ fixedSize: false } satisfies Partial<ImageInputProps>, rawProps);
  const { open } = useModalManager();
  const [isLoading, setIsLoading] = createSignal(true);
  const [isError, setIsError] = createSignal(false);

  const { t } = useTranslation();
  const [localProps, otherProps] = splitProps(props, [
    "progress",
    "setProgress",
    "onDelete",
    "mediaUpload",
    "fetchPage",
    "onCancel",
    "onSelect",
    "onClear",
    "defaultTab",
  ]);

  const openMediaModal = () =>
    open({
      content: () => (
        <MediaModal
          {...localProps}
          onSelect={(file) => {
            props.onSelect(file);
            setIsError(false);
            setIsLoading(true);
          }}
        />
      ),
    });

  return (
    <Input {...otherProps}>
      {(id) => (
        <Show
          when={!isNil(props.value)}
          fallback={
            <TextAndIconButton
              id={id}
              disabled={!isNil(props.value)}
              text={props.buttonLabel}
              onClick={() => openMediaModal()}
              icon="vector"
              variant="secondary"
            />
          }
        >
          <div
            class="relative flex items-center justify-center overflow-hidden rounded-sm border"
            classList={{
              "bg-surface-extra-subtle": isError() || isLoading(),
              "border-error": isError(),
              "border-default": !isError(),
              "h-45 w-74": props.fixedSize === true,
              "min-h-45": props.fixedSize === false,
            }}
          >
            <Show when={!isError() && !isLoading()}>
              <div class="absolute top-2 right-2">
                <IconButton
                  variant="secondary"
                  icon="trash"
                  size="medium"
                  onClick={props.onClear}
                  aria-label={t(($) => $.DELETE_BUTTON)}
                />
              </div>
            </Show>

            <Show when={isLoading() && !isError()}>
              <LoadingState name={props.value?.name} extension={props.value?.extension} />
            </Show>

            <Show when={isError()}>
              <ErrorState onClick={() => openMediaModal()} />
            </Show>

            <img
              class="h-full max-h-127 overflow-hidden object-contain"
              aria-hidden={isLoading() || isError()}
              classList={{ hidden: isLoading() || isError() }}
              src={props.value?.source}
              alt={props.value?.name}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsError(true)}
            />
          </div>
        </Show>
      )}
    </Input>
  );
};

type LoadingStateProps = {
  name?: string;
  extension?: string;
};
const LoadingState = (props: LoadingStateProps) => {
  const { t } = useTranslation();
  return (
    <div class="flex flex-col items-center justify-center p-6 text-center">
      <img class="h-10" src={brokenImage} alt={DECORATIVE_IMAGE_ALT} />
      <Text
        as="p"
        text={t(($) => $.IMAGE_INPUT_LOADING, { image: !isNil(props.name) ? `${props.name}.${props.extension}` : "" })}
        truncate={5}
        class="mt-1 mb-3 max-h-25 text-base-regular text-secondary"
      />
    </div>
  );
};

type ErrorStateProps = {
  onClick: () => void;
};
const ErrorState = (props: ErrorStateProps) => {
  const { t } = useTranslation();
  return (
    <div class="flex flex-col items-center justify-center p-6 text-center">
      <img class="h-10" src={brokenImage} alt={DECORATIVE_IMAGE_ALT} />
      <Text
        as="p"
        text={t(($) => $.IMAGE_INPUT_LOADING_FAIL)}
        truncate={5}
        class="mt-1 mb-3 max-h-25 text-base-regular text-secondary"
      />
      <TextAndIconButton
        size="medium"
        text={t(($) => $.IMAGE_INPUT_REPLACE_IMAGE)}
        onClick={props.onClick}
        icon="refresh"
        variant="secondary"
      />
    </div>
  );
};

export default ImageInput;
